import { useState, useEffect, useCallback } from 'react';
import type { DomainConfig, SenderAccount } from '../layout/AccountSwitcher';
import { authFetch } from '../../utils/authFetch';

const REGION_OPTIONS = [
    { value: 'LAT', label: "拉丁美洲", icon: '🌎' },
    { value: 'US', label: "北美洲", icon: '🇺🇸' },
    { value: 'EU', label: "欧洲", icon: '🇪🇺' },
    { value: 'ASIA', label: "亚太地区", icon: '🌏' },
    { value: 'AE', label: "中东", icon: '🕌' },
    { value: 'AF', label: "非洲", icon: '🌍' },
];

const REGION_ICONS: Record<string, string> = Object.fromEntries(REGION_OPTIONS.map(r => [r.value, r.icon]));
const REGION_LABELS: Record<string, string> = Object.fromEntries(REGION_OPTIONS.map(r => [r.value, r.label]));

interface DomainData {
    domain: string;
    region: string;
    label: string;
    senders: Array<{
        email: string;
        displayName: string;
        dailySendLimit: number;
        domain: string;
        sentToday: number;
        hasPassword: boolean;
    }>;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'error';

interface Props {
    accounts: DomainConfig[];
}

export function DomainsPage({ accounts: _staticAccounts }: Props) {
    const [domains, setDomains] = useState<DomainData[]>([]);
    const [loading, setLoading] = useState(true);

    // Add domain
    const [showAddDomain, setShowAddDomain] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [newRegion, setNewRegion] = useState('LAT');

    // Add sender
    const [showAddSender, setShowAddSender] = useState<string | null>(null);
    const [newEmail, setNewEmail] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [addingSender, setAddingSender] = useState(false);

    // Test SMTP
    const [testStatus, setTestStatus] = useState<Record<string, TestStatus>>({});
    const [testResult, setTestResult] = useState<Record<string, string>>({});

    // Send test
    const [showTestSend, setShowTestSend] = useState<string | null>(null);
    const [testToEmail, setTestToEmail] = useState('');
    const [testSending, setTestSending] = useState(false);

    const fetchDomains = useCallback(async () => {
        try {
            const res = await authFetch('/domains');
            const data = await res.json();
            if (data.success) setDomains(data.domains || []);
        } catch { }
        setLoading(false);
    }, []);

    useEffect(() => { fetchDomains(); }, [fetchDomains]);

    const totalSenders = domains.reduce((sum, d) => sum + d.senders.length, 0);
    const totalLimit = domains.reduce((sum, d) => sum + d.senders.reduce((s, a) => s + a.dailySendLimit, 0), 0);

    const handleAddDomain = async () => {
        if (!newDomain) return;
        try {
            const res = await authFetch('/domains', {
                method: 'POST',
                body: JSON.stringify({
                    domain: newDomain,
                    region: newRegion,
                    label: REGION_LABELS[newRegion] || newRegion,
                }),
            });
            const data = await res.json();
            if (data.success) {
                await fetchDomains();
                setShowAddDomain(false);
                setNewDomain('');
            } else alert(`Error: ${data.error}`);
        } catch { alert("无法连接服务器"); }
    };

    const handleDeleteDomain = async (domain: string) => {
        if (!window.confirm(`确定删除域名 ${domain} 及其全部发件账号吗？`)) return;
        try {
            await authFetch(`/domains/${domain}`, { method: 'DELETE' });
            await fetchDomains();
        } catch { alert("无法连接服务器"); }
    };

    const handleAddSender = async () => {
        if (!showAddSender || !newEmail || !newPassword) return;
        setAddingSender(true);
        const fullEmail = `${newEmail}@${showAddSender}`;
        try {
            const res = await authFetch(`/domains/${showAddSender}/senders`, {
                method: 'POST',
                body: JSON.stringify({
                    email: fullEmail,
                    displayName: newDisplayName || `${newEmail} | Lawhelper`,
                    password: newPassword,
                }),
            });
            const data = await res.json();
            if (data.success) {
                await fetchDomains();
                setShowAddSender(null);
                setNewEmail(''); setNewDisplayName(''); setNewPassword('');
            } else alert(`Error: ${data.error}`);
        } catch { alert("无法连接服务器"); }
        setAddingSender(false);
    };

    const handleDeleteSender = async (domain: string, email: string) => {
        if (!window.confirm(`确定移除 ${email} 吗？`)) return;
        try {
            await authFetch(`/domains/${domain}/senders/${encodeURIComponent(email)}`, { method: 'DELETE' });
            await fetchDomains();
        } catch { alert("无法连接服务器"); }
    };

    const testSmtp = async (email: string) => {
        setTestStatus(prev => ({ ...prev, [email]: 'testing' }));
        try {
            const res = await authFetch('/domains/test-smtp', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            setTestStatus(prev => ({ ...prev, [email]: data.success ? 'success' : 'error' }));
            setTestResult(prev => ({ ...prev, [email]: data.success ? '✅ SMTP OK' : `❌ ${data.error}` }));
        } catch {
            setTestStatus(prev => ({ ...prev, [email]: 'error' }));
            setTestResult(prev => ({ ...prev, [email]: "❌ 无法连接服务器" }));
        }
    };

    const sendTestEmail = async (fromEmail: string) => {
        if (!testToEmail) return;
        setTestSending(true);
        try {
            const res = await authFetch('/domains/test-send', {
                method: 'POST',
                body: JSON.stringify({ fromEmail, toEmail: testToEmail }),
            });
            const data = await res.json();
            alert(data.success ? `✅ 已发送至 ${testToEmail}` : `❌ ${data.error}`);
        } catch { alert("❌ 无法连接服务器"); }
        setTestSending(false);
        setShowTestSend(null);
        setTestToEmail('');
    };

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>正在加载域名…</div>;

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 className="page-title">🌐 域名与账号</h2>
                    <p className="page-subtitle">{domains.length} 个域名 · {totalSenders} 个发件账号 · SpaceMail SMTP</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAddDomain(true)}>
                    + 添加域名
                </button>
            </div>

            {/* SMTP Config */}
            <div className="card" style={{ marginBottom: 16, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>SMTP 服务器</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 12 }}>
                        📡 mail.spacemail.com:465 (SSL)
                    </span>
                </div>
                <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontWeight: 600 }}>
                    Spacemail
                </span>
            </div>

            {/* Stats */}
            <div className="grid-4" style={{ marginBottom: 24 }}>
                <div className="card"><div className="stat-value">{domains.length}</div><div className="stat-label">域名与账号</div></div>
                <div className="card"><div className="stat-value">{totalSenders}</div><div className="stat-label">发件账号</div></div>
                <div className="card"><div className="stat-value">{domains.filter(d => d.senders.some(s => s.hasPassword)).length}</div><div className="stat-label">可用</div></div>
                <div className="card"><div className="stat-value">{totalLimit}</div><div className="stat-label">每日发送额度</div></div>
            </div>

            {/* Empty state */}
            {domains.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>尚未配置域名</h3>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                        添加发件域名后即可配置邮件发送。
                    </p>
                    <button className="btn btn-primary" onClick={() => setShowAddDomain(true)}>+ 添加域名</button>
                </div>
            )}

            {/* Domain cards */}
            <div className="grid-2" style={{ gap: 16 }}>
                {domains.map(dc => (
                    <div key={dc.domain} className="domain-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span className="domain-flag">{REGION_ICONS[dc.region] || '🌍'}</span>
                                <div>
                                    <div className="domain-name">{dc.domain}</div>
                                    <div className="domain-meta">{dc.label || REGION_LABELS[dc.region] || dc.region}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }}
                                    onClick={() => setShowAddSender(dc.domain)}>
                                    + 添加账号
                                </button>
                                <button className="btn btn-outline btn-sm" style={{ fontSize: 10, color: '#ef4444' }}
                                    onClick={() => handleDeleteDomain(dc.domain)}>
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Senders */}
                        {dc.senders.length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                                暂无发件账号，请添加第一个账号。
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {dc.senders.map(s => (
                                    <div key={s.email} style={{
                                        padding: '8px 10px',
                                        background: 'var(--bg-input)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 12,
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.email}</span>
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                <span style={{
                                                    fontSize: 9, padding: '1px 5px', borderRadius: 3,
                                                    background: s.hasPassword ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                                    color: s.hasPassword ? '#22c55e' : '#ef4444',
                                                }}>
                                                    {s.hasPassword ? '🔑' : "⚠️ 未配置密码"}
                                                </span>
                                                <button className="btn btn-outline btn-sm"
                                                    style={{ fontSize: 9, padding: '1px 6px' }}
                                                    disabled={testStatus[s.email] === 'testing'}
                                                    onClick={() => testSmtp(s.email)}>
                                                    {testStatus[s.email] === 'testing' ? '⏳' :
                                                        testStatus[s.email] === 'success' ? '✅' :
                                                            testStatus[s.email] === 'error' ? '❌' : '🔌'} 测试
                                                </button>
                                                <button className="btn btn-outline btn-sm"
                                                    style={{ fontSize: 9, padding: '1px 6px' }}
                                                    onClick={() => setShowTestSend(s.email)}>
                                                    📧
                                                </button>
                                                <button className="btn btn-outline btn-sm"
                                                    style={{ fontSize: 9, padding: '1px 6px', color: '#ef4444' }}
                                                    onClick={() => handleDeleteSender(dc.domain, s.email)}>
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                        <div style={{ color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{s.displayName}</span>
                                            <span>{s.sentToday}/{s.dailySendLimit}</span>
                                        </div>
                                        {testResult[s.email] && (
                                            <div style={{
                                                marginTop: 4, fontSize: 10, padding: '3px 6px', borderRadius: 4,
                                                background: testStatus[s.email] === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                                color: testStatus[s.email] === 'success' ? '#22c55e' : '#ef4444',
                                            }}>
                                                {testResult[s.email]}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* ── Add Domain Modal ── */}
            {showAddDomain && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAddDomain(false); }}>
                    <div className="modal-panel" style={{ maxWidth: 420 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>🌐 添加域名</h3>
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">域名</label>
                            <input className="input" value={newDomain}
                                onChange={(e) => setNewDomain(e.target.value)}
                                placeholder="例如：lawhelper.lat" />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">地区</label>
                            <select className="input" value={newRegion} onChange={(e) => setNewRegion(e.target.value)}>
                                {REGION_OPTIONS.map(r => (
                                    <option key={r.value} value={r.value}>{r.icon} {r.label}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn btn-outline" onClick={() => setShowAddDomain(false)}>取消</button>
                            <button className="btn btn-primary" disabled={!newDomain} onClick={handleAddDomain}>
                                添加域名
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Add Sender Modal ── */}
            {showAddSender && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAddSender(null); }}>
                    <div className="modal-panel" style={{ maxWidth: 420 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📧 添加发件人至 {showAddSender}</h3>
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">邮箱用户名</label>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <input className="input" style={{ flex: 1 }} value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="leo" />
                                <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>@{showAddSender}</span>
                            </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">显示名称</label>
                            <input className="input" value={newDisplayName}
                                onChange={(e) => setNewDisplayName(e.target.value)}
                                placeholder="Leo | Lawhelper" />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">SpaceMail 邮箱密码</label>
                            <input className="input" type="password" value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="邮箱登录密码" />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn btn-outline" onClick={() => { setShowAddSender(null); setNewEmail(''); setNewDisplayName(''); setNewPassword(''); }}>
                                取消
                            </button>
                            <button className="btn btn-primary" disabled={!newEmail || !newPassword || addingSender}
                                onClick={handleAddSender}>
                                {addingSender ? "⏳ 正在添加…" : "添加发件账号"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Test Send Modal ── */}
            {showTestSend && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowTestSend(null); setTestToEmail(''); } }}>
                    <div className="modal-panel" style={{ maxWidth: 420 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>📧 发送测试邮件</h3>
                        <div style={{ marginBottom: 12 }}>
                            <label className="section-title">发件人</label>
                            <div className="input" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                                {showTestSend}
                            </div>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label className="section-title">收件人（你的邮箱）</label>
                            <input className="input" value={testToEmail}
                                onChange={(e) => setTestToEmail(e.target.value)}
                                placeholder="your-email@gmail.com" />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="btn btn-outline" onClick={() => { setShowTestSend(null); setTestToEmail(''); }}>取消</button>
                            <button className="btn btn-primary" disabled={testSending || !testToEmail}
                                onClick={() => sendTestEmail(showTestSend!)}>
                                {testSending ? "⏳ 正在发送…" : "📤 发送测试邮件"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Setup Guide */}
            <div className="card" style={{ marginTop: 24, padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📋 快速配置</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                    <p><strong>1.</strong> 点击 <strong>“+ 添加域名”</strong> → 输入域名（例如 lawhelper.lat）</p>
                    <p><strong>2.</strong> 点击 <strong>“+ 添加账号”</strong> → 输入邮箱用户名与 SpaceMail 密码</p>
                    <p><strong>3.</strong> 点击 <strong>“🔌 测试”</strong> → 验证 SMTP 连接</p>
                    <p><strong>4.</strong> 点击 <strong>"📧"</strong> → 向自己发送测试邮件</p>
                    <p><strong>5.</strong> 进入 <strong>联系人</strong> → <strong>写邮件</strong> → 开始批量发送</p>
                </div>
            </div>
        </div>
    );
}
